import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Building2,
  CalendarCheck2,
  Clock,
  Gem,
  Hammer,
  MessageCircle,
  Phone,
  SlidersHorizontal,
  Sparkles,
  Store,
} from "lucide-react";
import Banner from "@/components/Banner";
import { Eyebrow, GoldButton } from "@/components/Reveal";
import { PHOTOS, PHONE_DISPLAY, PHONE_LINK } from "@/data/content";

const PREVIEW = [
  {
    icon: Building2,
    title: "Aires communes",
    desc: "Halls, corridors, escaliers et ascenseurs d'immeubles.",
  },
  {
    icon: Briefcase,
    title: "Entretien de bureaux",
    desc: "Postes de travail, salles de réunion, sanitaires.",
  },
  {
    icon: Store,
    title: "Locaux commerciaux",
    desc: "Surfaces et espaces accueillant la clientèle.",
  },
  {
    icon: Hammer,
    title: "Après construction",
    desc: "Retrait des poussières et nettoyage de finition.",
  },
  {
    icon: CalendarCheck2,
    title: "Entretien régulier",
    desc: "Quotidien, hebdomadaire ou mensuel, selon vos besoins.",
  },
  {
    icon: Sparkles,
    title: "Nettoyage spécialisé",
    desc: "Tapis, vitres, garages et plus encore.",
  },
];

const WHY = [
  {
    icon: Gem,
    title: "Souci du détail",
    desc: "Chaque recoin est traité avec exigence, pour une finition impeccable.",
  },
  {
    icon: SlidersHorizontal,
    title: "Entretien adapté",
    desc: "Des protocoles ajustés à vos locaux et à la fréquence souhaitée.",
  },
  {
    icon: MessageCircle,
    title: "Communication claire",
    desc: "Un interlocuteur dédié et des échanges rapides et fluides.",
  },
  {
    icon: Clock,
    title: "Fiabilité",
    desc: "Le respect des horaires et des engagements convenus.",
  },
];

export default function Home() {
  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <Banner
        image={PHOTOS.hero}
        alt="Hall d'immeuble prestigieux entretenu par PUREVA"
        title={["L'excellence", "du nettoyage", "professionnel."]}
        text="PUREVA prend soin de vos aires communes, bureaux et espaces commerciaux à Montréal et sur la Rive-Sud."
        testid="hero-title"
        full
      >
        <GoldButton as={Link} to="/services" data-testid="hero-cta-services">
          Découvrir nos services
          <ArrowRight className="h-4 w-4" />
        </GoldButton>
      </Banner>

      {/* ---------------- SERVICES PREVIEW ---------------- */}
      <section
        className="bg-[#F4EFE6] py-20 lg:py-28"
        data-testid="services-preview-section"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="font-serif text-3xl tracking-[0.08em] text-noir sm:text-4xl">
              NOS SERVICES
            </h2>
            <div className="mx-auto mt-4 h-px w-16 bg-gold" />
            <p className="mt-5 text-[15px] text-neutral-600 sm:text-base">
              Des solutions de nettoyage adaptées à vos besoins
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 xl:grid-cols-6">
            {PREVIEW.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.07 }}
                className="rounded-lg border border-neutral-200 bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-gold/60"
              >
                <Icon className="mx-auto h-9 w-9 text-gold" strokeWidth={1.4} />
                <h3 className="mt-4 text-[12px] font-bold uppercase leading-snug tracking-[0.08em] text-noir">
                  {title}
                </h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-neutral-500">
                  {desc}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="mt-12 text-center"
          >
            <Link
              to="/services"
              data-testid="services-preview-link-all"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-noir px-9 py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors duration-300 hover:bg-surface"
            >
              Voir tous nos services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ---------------- WHY CHOOSE US ---------------- */}
      <section className="bg-noir" data-testid="why-us-section">
        <div className="grid lg:grid-cols-[38%_62%]">
          <div className="grain relative min-h-[260px] overflow-hidden lg:min-h-0">
            <img
              src={PHOTOS.why}
              alt="Espace de travail professionnel entretenu par PUREVA"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-noir/40 to-transparent lg:bg-gradient-to-r" />
          </div>
          <div className="px-6 py-20 sm:px-10 lg:px-16 lg:py-24">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <Eyebrow>Notre engagement</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl tracking-[0.06em] text-ivory sm:text-4xl">
                POURQUOI CHOISIR <span className="text-gold">PUREVA</span> ?
              </h2>
              <div className="mx-auto mt-5 h-px w-20 bg-gold" />
            </motion.div>

            <div className="mt-14 grid gap-10 sm:grid-cols-2 xl:grid-cols-4">
              {WHY.map(({ icon: Icon, title, desc }, i) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="text-center"
                >
                  <Icon className="mx-auto h-9 w-9 text-gold" strokeWidth={1.4} />
                  <h3 className="mt-4 text-[12px] font-bold uppercase leading-snug tracking-[0.1em] text-ivory">
                    {title}
                  </h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-mist">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- ABOUT TEASER ---------------- */}
      <section
        className="bg-[#F4EFE6] py-20 lg:py-28"
        data-testid="about-teaser-section"
      >
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <img
              src={PHOTOS.teaser}
              alt="Hall d'immeuble propre et lumineux entretenu par PUREVA"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            <span className="absolute left-0 top-0 h-14 w-px bg-gold" />
            <span className="absolute left-0 top-0 h-px w-14 bg-gold" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Eyebrow>À propos</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-tight text-noir sm:text-4xl">
              PUREVA, le soin du détail
            </h2>
            <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-neutral-600 sm:text-base">
              PUREVA est une entreprise de nettoyage au service des
              gestionnaires d'immeubles et des entreprises à Montréal et sur la
              Rive-Sud. Notre objectif est de contribuer à des espaces propres,
              accueillants et bien entretenus.
            </p>
            <Link
              to="/a-propos"
              data-testid="about-teaser-link"
              className="group mt-8 inline-flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#9a7b3f]"
            >
              Découvrir notre approche
              <span className="h-px w-10 bg-gold transition-all duration-300 group-hover:w-16" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="bg-noir py-20 lg:py-24" data-testid="bottom-cta-quote">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9 }}
            className="flex flex-col items-start gap-8 border border-gold/25 bg-surface px-8 py-10 sm:px-12 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-gold text-noir">
                <Phone className="h-7 w-7" />
              </span>
              <div>
                <h2 className="font-serif text-2xl leading-snug text-gold sm:text-3xl">
                  Besoin d'un service de nettoyage ?
                </h2>
                <p className="mt-2 max-w-md text-[15px] text-mist">
                  Contactez-nous dès aujourd'hui pour une soumission claire et
                  personnalisée.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <a
                href={PHONE_LINK}
                data-testid="bottom-cta-phone"
                className="inline-flex items-center gap-2.5 text-[15px] font-medium tracking-[0.08em] text-ivory transition-colors hover:text-gold"
              >
                <Phone className="h-4 w-4 text-gold" />
                {PHONE_DISPLAY}
              </a>
              <GoldButton as={Link} to="/contact" data-testid="bottom-cta-button">
                Demander une soumission
                <ArrowRight className="h-4 w-4" />
              </GoldButton>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
